import React, { useMemo } from 'react'
import { Button, DataGridTable, Title } from '@/ui'
import styles from '../additionalContracts/AdditionalContractStyles.module.scss'
import tableStyles from './SocialLeaveStyles.module.scss'
import { Edit, Pencil, Trash } from '@app/icons'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '@/i18next'
import { getSocialLeaveDataByType } from '@features/contracts/socialLeaves/utils'
import { Modal, ModalBackButton } from '@/components/Modal'
import { SignStatus } from '@features/contracts/badges'
import { useSelector } from 'react-redux'
import { contractsSelectors, userSelectors } from '@/entity'
import cl from 'classnames'
import { LANGUAGE_ENUM } from '@/shared'

export const ViewSocialLeaveModal = ({ socialLeave, onClose, onDelete, onEdit, onSign, onEarlyExit, onDeleteEarlyExit }) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const lang = getCurrentLanguage()

	const data = React.useMemo(() => getSocialLeaveDataByType(socialLeave, t, lang), [lang, socialLeave, t])
	const contract = useSelector(contractsSelectors.contract)
	const status = useMemo(() => ({ code: socialLeave?.status === 'NOT_SIGNED' ? 2 : 1 }), [socialLeave])
	const activeRole = useSelector(userSelectors.activeRole)

	const esutdRegErrors = useSelector((state) => state.handbook.esutdRegErrors)

	const errorData = esutdRegErrors.find((item) => item.code == socialLeave?.desutdRegErrorCode)

	const earlyExitData = () => {
		let status = {
			'SIGNED': t('signed_status'),
			'NOT_SIGNED': t('pending_sign'),
		}

		let reason = {
			[LANGUAGE_ENUM.rus]: socialLeave?.earlyExitSocialLeave?.earlyreasonRu,
			[LANGUAGE_ENUM.kz]: socialLeave?.earlyExitSocialLeave?.earlyreasonKz,
		}

		let res = [
			{ title: t('profUnions.unionStatus'), data: status[socialLeave?.earlyExitSocialLeave?.status] || '' },
		]

		if (socialLeave?.entryDate) {
			res.push({ title: t('esutd_reg_date'), data: socialLeave?.entryDate })
		}

		if (socialLeave?.earlyExitSocialLeave?.earlyEndDate) {
			res.push({ title: t('social_leave.fact_work_date_label'), data: socialLeave?.earlyExitSocialLeave?.earlyEndDate })
		}

		if (socialLeave?.earlyExitSocialLeave?.earlyWorkDate) {
			res.push({ title: t('vacation_date_work_start'), data: socialLeave?.earlyExitSocialLeave?.earlyWorkDate })
		}

		if (reason[lang]) {
			res.push({ title: t('social_leave.early.reason'), data: reason[lang]})
		}

		return res
	}

	return (
		<Modal windowClassName={styles.socialLeaveModal}>
			<ModalBackButton onClick={onClose} />
			<div className={`${styles.wrapper} scroll`}>
				<div className={styles.header}>
					<Title className={styles.title}>
						{t('social_leave.view_title', {
							contractNumber: contract?.contractNumber,
							entryDate: contract?.dateFrom,
						})}
					</Title>
					<div className={styles.headerRight}>
						<SignStatus item={status} />
					</div>
				</div>
				<p>
					{!socialLeave.desutdRegErrorCode && (
						<>
							{t('social_leave.entryDate')}: {socialLeave.entryDate}
						</>
					)}

					{socialLeave.desutdRegErrorCode && (
						<p className={styles.registrationDate}>
							{t('social_leave.entryDate')}:{' '}
							<span className={styles.error}>
								{t('package_loading.contract_error_text', {
									errorText: errorData?.[language],
									errorNumber: errorData?.code,
								})}
							</span>
						</p>
					)}
				</p>
				<div className={styles.actions}>
					{activeRole === 'ROLE_EMPLOYER' &&
						socialLeave?.dcontractState?.code !== '1' &&
						contract?.dcontractStatus?.code !== '2' && (
							<Button textColor="green" buttonClassName={styles.button} onClick={onSign}>
								<Pencil className={styles.iconWhite} />
								{t('sign_button')}
							</Button>
						)}
					{activeRole === 'ROLE_EMPLOYER' && contract?.dcontractStatus?.code !== '2' && (
						<Button borderColor="green" transparent border buttonClassName={styles.button} onClick={onEdit}>
							<Edit className={styles.iconGreen} />
							{t('edit_action')}
						</Button>
					)}
					{activeRole === 'ROLE_EMPLOYER' && contract?.dcontractStatus?.code !== '2' && (
						<Button type="bare" buttonClassName={styles.button} onClick={onDelete}>
							<Trash className={styles.iconGrey} />
							<span>{t('delete_action')}</span>
						</Button>
					)}
				</div>
				<DataGridTable
					title={t('vacation_info')}
					information={data}
					hideSubTitle
					titleClass={tableStyles.tableTitleCell}
				/>

				<div className={cl('mt-5')}>
					{activeRole === 'ROLE_EMPLOYER' && socialLeave?.earlyExitSocialLeave ? (
						<>
							<Title>{t('social_leave.early.title')}</Title>

							{
								socialLeave?.status !== 'NOT_SIGNED' &&
								<div className={styles.actions}>
									<Button borderColor="green" transparent border buttonClassName={styles.button}
													onClick={onEarlyExit}>
										<Edit className={styles.iconGreen} />
										{t('edit_action')}
									</Button>

									<Button type="bare" buttonClassName={styles.button} onClick={onDeleteEarlyExit}>
										<Trash className={styles.iconGrey} />
										<span>{t('delete_action')}</span>
									</Button>
								</div>
							}
							<DataGridTable
								hideTitle
								information={earlyExitData()}
								hideSubTitle
								titleClass={tableStyles.tableTitleCell}
							/>
						</>
					) : (
						socialLeave?.status !== 'NOT_SIGNED' &&
						<>
							<Title>{t('social_leave.early.title')}</Title>

							<div className={styles.actions}>
								<Button borderColor="green" transparent border buttonClassName={styles.button} onClick={onEarlyExit}>
									<Edit className={styles.iconGreen} />
									{t('social_leave.early.add')}
								</Button>
							</div>
						</>

					)}
				</div>


				<Button borderColor="green" buttonClassName={styles.closeButton} onClick={onClose}>
					{t('social_leave.close')}
				</Button>
			</div>
		</Modal>
	)
}
